import { useCallback, useEffect, useState } from 'react'
import {
  Button, HStack, NumberInput, Spinner, Stack, Text, VStack, type NumberInputValueChangeDetails,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { toaster } from '@/components/ui/toaster'
import { useFollowUpRoutes, useCutFollowUpRoutes, useRecutFollowUpBatch } from '../../../../hooks/useFollowUpRoutes'
import { useJobPolling } from '../../../../hooks/useJobPolling'
import { useLimits } from '../../../../hooks/useLimits'
import { extractApiMessage } from '../../../../lib/apiError'
import { FollowUpRouteBatchList } from './FollowUpRouteBatchList'
import { FollowUpRoutePreviewMap } from './FollowUpRoutePreviewMap'
import type { FollowUpKind, FollowUpRouteCutResultDto } from '../../../../types/followUp'

export interface FollowUpRouteActionsProps {
  campaignId: string
  kind: FollowUpKind
  customKindId?: string | null
  /** P5 override: enqueue a different cut (e.g. widened chase batch) and return its job id. */
  enqueue?: (driverCount: number) => Promise<{ jobId: string }>
  /** P5 (ruling R18): hide the cut row and keep only the batch list, preview, assign and re-cut —
   *  the Chase section's own prepare → confirm → cut flow is the only way to cut chase routes. */
  hideCut?: boolean
}

/** CF-1 P2: a route-mode Follow-up section's actions — cut the open requests into N driving routes
 *  (a background job), list the batches, preview the selected batch, assign a route. */
export function FollowUpRouteActions({ campaignId, kind, customKindId = null, enqueue, hideCut = false }: FollowUpRouteActionsProps) {
  const queryClient = useQueryClient()
  const { data: routes, isLoading, error } = useFollowUpRoutes(campaignId, kind, customKindId)
  const { mutateAsync: cutRoutes, isPending: isCutPending } = useCutFollowUpRoutes(campaignId)
  const { mutateAsync: recutBatch, isPending: isRecutPending } = useRecutFollowUpBatch(campaignId)

  // Caps are pre-flight only: while limits load there is no clamp and no warning.
  const inputCaps = useLimits().data?.inputCaps
  const maxDrivers = inputCaps?.maxFollowUpCutDrivers
  const maxItems = inputCaps?.maxFollowUpCutItems
  const clampDrivers = useCallback((n: number) => {
    const v = Number.isFinite(n) ? Math.max(1, Math.round(n)) : 1
    return maxDrivers !== undefined ? Math.min(v, maxDrivers) : v
  }, [maxDrivers])

  const [driversText, setDriversText] = useState('1')
  const drivers = clampDrivers(Number(driversText))
  const [jobId, setJobId] = useState<string | null>(null)
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null)

  const { data: job, isTerminal } = useJobPolling(jobId)
  const isRunning = isCutPending || isRecutPending || (!!jobId && !isTerminal)

  useEffect(() => {
    if (!isTerminal) return
    if (job?.status === 'Succeeded') {
      // Invalidate on the job's success, never at the 202 accept (job-polling rules).
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'itinerary'] })
      const result = job.result as FollowUpRouteCutResultDto | undefined
      const routeCount = result?.routeCount ?? 0
      const skipped = result?.skippedItemCount ?? 0
      if (routeCount === 0 && skipped > 0) {
        // Every eligible item was walk-excluded (no mappable address, DNC, deceased): they stay Open
        // and unrouted, so "already routed" would be wrong on every retry.
        toaster.create({
          title: `None of these could be routed — ${skipped} have no mappable address or can't be contacted.`,
          type: 'info',
          duration: 7000,
        })
      } else if (routeCount === 0) {
        toaster.create({ title: 'Nothing left to cut — these were already routed.', type: 'info', duration: 5000 })
      } else {
        toaster.create({ title: routeCount === 1 ? '1 route cut' : `${routeCount} routes cut`, type: 'success', duration: 4000 })
        if (result?.turfMapId) setSelectedBatchId(result.turfMapId)
      }
    } else {
      // Job Failed, or the poll itself errored (e.g. 404): surface it and allow a retry.
      toaster.create({
        title: 'Route cut failed',
        description: job?.errorMessage ?? 'An error occurred.',
        type: 'error',
        duration: 7000,
      })
    }
    setJobId(null)
    // Fire once per terminal transition only: queryClient/campaignId are stable for the mount, and
    // adding them would re-toast after setJobId(null) resets the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerminal, job])

  const startJob = async (run: () => Promise<{ jobId: string }>, failureTitle: string) => {
    try {
      const { jobId: newJobId } = await run()
      setJobId(newJobId)
    } catch (err: unknown) {
      toaster.create({
        title: failureTitle,
        description: extractApiMessage(err, 'Please try again.'),
        type: 'error',
        duration: 7000,
      })
    }
  }

  const handleCut = () => startJob(() =>
    enqueue
      ? enqueue(drivers)
      : cutRoutes({ kind, customKindId, itemIds: null, driverCount: drivers }),
  'Could not cut routes')

  const handleRecut = (turfMapId: string, driverCount: number) =>
    startJob(() => recutBatch({ turfMapId, driverCount }), 'Could not re-cut routes')

  if (isLoading) {
    return <VStack py={6}><Spinner color="accent" /></VStack>
  }
  if (!!error || !routes) {
    return <Text color="danger.fg" fontSize="sm">{extractApiMessage(error, 'Could not load routes.')}</Text>
  }

  const unrouted = routes.unroutedOpenCount
  const overItemCap = maxItems !== undefined && !enqueue && unrouted > maxItems
  const batches = routes.batches
  const selected = batches.find(b => b.turfMapId === selectedBatchId) ?? batches[0]
  if (hideCut && batches.length === 0) return null

  return (
    <VStack align="stretch" gap={4} w="full" minW={0}>
      {!hideCut && (<>
        <Stack direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} gap={2}>
          <HStack gap={2} flexWrap="wrap">
            <Text fontSize="sm">Cut {unrouted} open requests for</Text>
            <NumberInput.Root
              size="sm"
              w="88px"
              min={1}
              max={maxDrivers}
              value={driversText}
              onValueChange={(d: NumberInputValueChangeDetails) => setDriversText(d.value)}
            >
              <NumberInput.Input aria-label="Drivers" minH="44px" />
              <NumberInput.Control>
                <NumberInput.IncrementTrigger />
                <NumberInput.DecrementTrigger />
              </NumberInput.Control>
            </NumberInput.Root>
            <Text fontSize="sm">drivers</Text>
          </HStack>
          <Button
            colorPalette="brand"
            size="sm"
            minH="44px"
            loading={isRunning}
            disabled={unrouted === 0 || isRunning}
            onClick={handleCut}
          >
            Cut routes
          </Button>
        </Stack>
        {overItemCap && (
          <Text fontSize="xs" color="info.fg">
            A single cut takes up to {maxItems} requests; the server will refuse this one.
          </Text>
        )}
      </>)}
      <FollowUpRouteBatchList
        campaignId={campaignId}
        batches={batches}
        selectedBatchId={selected?.turfMapId ?? null}
        onSelectBatch={setSelectedBatchId}
        onRecut={handleRecut}
        isBusy={isRunning}
        clampDrivers={clampDrivers}
        maxDrivers={maxDrivers}
      />
      {selected && <FollowUpRoutePreviewMap turfMapId={selected.turfMapId} routes={selected.routes} />}
    </VStack>
  )
}
